import { useMemo, useRef, useState } from 'react';

import { FormControl, MenuItem, Select, TextField } from '@mui/material';
import { ClassNameMap } from '@mui/styles';

import { TableFilter } from '../../Table.types';

type EnumSearchFilterControlProps = {
  filter: TableFilter;
  activeFilters: Record<string, string>;
  classes: ClassNameMap;
  onFilterChange: (condition: string, value: any) => any;
};

export const EnumSearchFilterControl = ({
  filter,
  activeFilters,
  classes,
  onFilterChange,
}: EnumSearchFilterControlProps) => {
  const selectControlRef = useRef<HTMLDivElement | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const conditionKey = filter.conditions[0]?.key || 'equals';

  const filteredValues = useMemo(() => {
    const normalizedSearchValue = searchValue.trim().toLowerCase();
    if (!normalizedSearchValue) {
      return filter.values || [];
    }

    return (filter.values || []).filter(value =>
      String(value).toLowerCase().includes(normalizedSearchValue),
    );
  }, [filter.values, searchValue]);

  return (
    <FormControl ref={selectControlRef} className={classes.selectFormControl}>
      <Select
        data-testid="enum-search-filter"
        MenuProps={{
          disablePortal: true,
          anchorEl: selectControlRef.current,
          className: classes.selectMenuControl,
          PaperProps: {
            style: {
              width: 'auto',
              maxWidth: 'none',
            },
          },
          MenuListProps: {
            style: {
              whiteSpace: 'nowrap',
            },
          },
        }}
        labelId="curation-enum-search"
        id="curation-enum-search-select"
        value={activeFilters[conditionKey] || ''}
        onChange={event => {
          onFilterChange(conditionKey, event.target.value);
        }}
      >
        <MenuItem
          disableRipple
          onClick={event => event.stopPropagation()}
          style={{ cursor: 'default' }}
        >
          <TextField
            autoFocus
            placeholder="Search options"
            value={searchValue}
            onChange={event => setSearchValue(event.target.value)}
            onClick={event => event.stopPropagation()}
            onKeyDown={event => event.stopPropagation()}
            size="small"
            variant="outlined"
            fullWidth
          />
        </MenuItem>
        {filteredValues.length > 0 ? (
          filteredValues.map((value, index) => (
            <MenuItem data-testid={String(value)} key={index} value={value as string}>
              {String(value)}
            </MenuItem>
          ))
        ) : (
          <MenuItem key="none">No values found</MenuItem>
        )}
      </Select>
    </FormControl>
  );
};
